// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import parse from "html-react-parser";

import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Modal from "@insite/mobius/Modal";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import { useLocalStorage } from "@store/Framework/Common/Hooks/useLocalStorage";
import Button from "@store/MobiusOverrides/Button/Button";
import { useToaster } from "@store/MobiusOverrides/Toast/ToasterContext";
import React, { useRef } from "react";
import { css } from "styled-components";

enum fields {
    alertInterval = "alertInterval",
    modalSize = "modalSize",
    alertFormat = "alertFormat",
    toastPosition = "toastPosition",
    toastDuration = "toastDuration",
    modalTitle = "modalTitle",
    toastBorderRadius = "toastBorderRadius",
    toastType = "toastType",
}
interface OwnProps {
    fields: {
        [fields.alertInterval]: AlertInterval;
        [fields.modalSize]: ModalSize;
        [fields.alertFormat]: AlertFormat;
        [fields.toastPosition]: ToastPosition;
        [fields.toastDuration]: number;
        [fields.modalTitle]: string;
        [fields.toastBorderRadius]: string;
        [fields.toastType]: ToastType;
    };
}
type ToastType = "success" | "danger" | "info" | "warning" | "";
type ToastPosition = "top-right" | "top" | "top-left" | "bottom-right" | "bottom" | "bottom-left";
type AlertFormat = "Modal" | "Toast";
type AlertInterval = "once" | "daily" | "always";
type ModalSize = "small" | "medium" | "large";

interface AlertTimerState {
    lastAlert: Date;
}
type Props = WidgetProps & OwnProps;
const zoneName = "Alert Content";
const AlertWidget: React.FC<Props> = ({
    id,
    fields: {
        alertInterval,
        modalSize,
        alertFormat,
        toastDuration,
        toastPosition,
        modalTitle,
        toastBorderRadius,
        toastType,
    },
}) => {
    const hasShown = useRef(false);
    const toaster = useToaster();
    const { isInShell, isEditing } = useShellContext();
    const [showModal, setShowModal] = React.useState(false);
    const [alertTimerState, setAlertTimerState] = useLocalStorage<AlertTimerState>(id || "");
    const modalOverrides = modalTitle
        ? undefined
        : {
              modalTitle: css`
                  justify-content: end;
              `,
          };

    const addToast = () => {
        toaster.addToast({
            children: <Zone contentId={id} zoneName={zoneName} />,

            messageType: toastType || undefined,
            cssOverrides: {
                toast: css`
                    ${toastType === "" && `border: none;`}
                    ${toastBorderRadius && `border-radius: ${toastBorderRadius};`}
                    width: unset;
                `,
            },
            timeoutLength: toastDuration * 1000,
        });

        // cleanup to make sure we don't override the theme value for toast position
        setTimeout(() => toaster.setPosition?.(undefined), toastDuration * 1000 + 1500);
    };

    const handleDisplay = (format: AlertFormat) => {
        if (format === "Modal") {
            setTimeout(() => setShowModal(true), 2000);
        }
        if (format === "Toast") {
            toaster.setPosition?.(toastPosition);
            setTimeout(addToast, 2000);
        }
    };

    const handleShow = (hideInShell = true) => {
        if (hasShown.current) {
            return;
        }
        const lastAlert = new Date(alertTimerState?.lastAlert || "");
        const today = new Date();
        if (alertInterval === "daily" && lastAlert.getDate() !== today.getDate()) {
            handleDisplay(alertFormat);
            setAlertTimerState({
                lastAlert: new Date(),
            });
        } else if (alertInterval === "always") {
            handleDisplay(alertFormat);
        }
    };

    const shouldIShow = () => {
        let shouldIShow = false;

        if (isInShell || isEditing) {
            return false;
        }
        if (!alertTimerState) {
            shouldIShow = true;
        }
        if (alertInterval === "always") {
            shouldIShow = true;
        }
        if (alertInterval === "daily") {
            const lastAlert = new Date(alertTimerState?.lastAlert || "");
            const today = new Date();
            if (lastAlert.getDate() !== today.getDate()) {
                shouldIShow = true;
            }
        }
        return shouldIShow;
    };

    if (shouldIShow() && !hasShown.current) {
        handleShow();
        hasShown.current = true;
    }
    return (
        <>
            {isEditing && (
                <>
                    <Zone contentId={id} zoneName={zoneName} />
                    <Button onClick={() => handleDisplay(alertFormat)}>
                        {translate("Show")}
                        {alertFormat}
                    </Button>
                </>
            )}

            <Modal
                headline={parse(modalTitle, parserOptions)}
                sizeVariant={modalSize}
                handleClose={() => setShowModal(false)}
                isOpen={showModal}
                cssOverrides={modalOverrides}
            >
                <Zone contentId={id} zoneName={zoneName} />
            </Modal>
        </>
    );
};

const buttonBorderRadiusField = getCssUnitField(fields.toastBorderRadius);

const alertIntervalOptions: Option<AlertInterval>[] = [
    { displayName: "Once", value: "once" },
    { displayName: "Daily", value: "daily" },
    { displayName: "Each Page Load", value: "always" },
];

const modalSizeOptions: Option<ModalSize>[] = [
    { displayName: "Small", value: "small" },
    { displayName: "Medium", value: "medium" },
    { displayName: "Large", value: "large" },
];

const alertFormatOptions: Option<AlertFormat>[] = [
    { displayName: "Modal", value: "Modal" },
    { displayName: "Toast", value: "Toast" },
];

const toastPositionOptions: Option<ToastPosition>[] = [
    { displayName: "Top Right", value: "top-right" },
    { displayName: "Top", value: "top" },
    { displayName: "Top Left", value: "top-left" },
    { displayName: "Bottom Right", value: "bottom-right" },
    { displayName: "Bottom", value: "bottom" },
    { displayName: "Bottom Left", value: "bottom-left" },
];

const toastTypeOptions: Option<ToastType>[] = [
    { displayName: "Success", value: "success" },
    { displayName: "Danger", value: "danger" },
    { displayName: "Info", value: "info" },
    { displayName: "Warning", value: "warning" },
    { displayName: "Unstyled", value: "" },
];
const widgetModule: WidgetModule = {
    component: AlertWidget,
    definition: {
        group: "Common",
        displayName: "Page Alert",
        fieldDefinitions: [
            {
                name: fields.alertFormat,
                displayName: "Alert Format",
                editorTemplate: "DropDownField",
                options: alertFormatOptions,
                defaultValue: "Modal",
                fieldType: "General",
            },
            {
                name: fields.alertInterval,
                displayName: "Alert Interval",
                editorTemplate: "DropDownField",
                options: alertIntervalOptions,
                defaultValue: "once",
                fieldType: "General",
            },
            {
                name: fields.modalSize,
                displayName: "Modal Size",
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.alertFormat === "Modal",
                options: modalSizeOptions,
                defaultValue: "medium",
                fieldType: "General",
            },

            {
                name: fields.toastPosition,
                displayName: "Toast Position",
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.alertFormat === "Toast",
                options: toastPositionOptions,
                defaultValue: "top",
                fieldType: "General",
            },
            {
                name: fields.modalTitle,
                displayName: "Modal Title",
                editorTemplate: "RichTextField",
                fieldType: "General",
                isVisible: widget => widget.fields.alertFormat === "Modal",
                defaultValue: "Modal Title",
            },
            {
                name: fields.toastDuration,
                displayName: "Toast Duration (seconds)",
                editorTemplate: "IntegerField",
                isVisible: widget => widget.fields.alertFormat === "Toast",
                defaultValue: 2,
                fieldType: "General",
            },
            {
                displayName: "Toast Border Radius",
                ...buttonBorderRadiusField.definition,
                isVisible: widget => widget.fields.alertFormat === "toast",
            },
            {
                name: fields.toastType,
                displayName: "Toast Type",
                editorTemplate: "DropDownField",
                options: toastTypeOptions,
                defaultValue: "",
                isVisible: widget => widget.fields.alertFormat === "Toast",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
